import { createApp } from 'vue'
import BookingProcessApp from './BookingProcessApp.vue'
import router from './router/index'

import BookingServiceV1Impl from '@/api/services/BookingService/BookingServiceV1Impl'
import FunnelServiceV1Impl from '@/api/services/FunnelManagerService/FunnelServiceV1Impl'
import OptionFamiliesServiceV1Impl from '@/api/services/OptionFamiliesService/OptionFamiliesServiceV1Impl'
import CatalogServiceV1Impl from '@/api/services/CatalogService/CatalogServiceV1Impl'
import PriceServiceV1Impl from '@/api/services/PriceService/PriceServiceV1Impl'
import ContactServiceV1Impl from '@/api/services/ContactService/ContactServiceV1Impl'
import PaymentServiceV1Impl from '@/api/services/PaymentService/PaymentServiceV1Impl'
import OrchestratorV1Impl from '@/api/services/Orchestrator/OrchestratorV1Impl'
import PackageCompositionV1Impl from './api/services/PackageCompositionService/PackageCompositionV1Impl'
import TransportTimeLineV1Impl from './api/services/TransportTimeLineService/TransportTimeLineV1Impl'
import ServiceDurationV1Impl from './api/services/ServicesDurationService/ServiceDurationV1Impl'
import AccomodationServiceV1Impl from './api/services/AccomodationService/AccomodationServiceV1Impl'
import LocaleServiceImpl from './service/LocaleService/LocaleServiceImpl'

// state
import bookigFunnelSingleCompanyConfig from '@/state/bookigFunnelSingleCompanyConfig'

// localization
import localization from '@/localization'
// screensize
import ScreenSizeManager from "@/utils/screenSizeManager";
import initI18n from '@/service/translate'


// CSS
import 'bootstrap/dist/css/bootstrap.css'
import "@/css/styles.css"

// JS
// axios middleware
import '@/api/middleware/refresh.interceptor'
import getCookie from './utils/getCookie'

import getEnvFile from './logic/getEnvFile'
import { injectHeaderFooter } from './logic/headerFooter'
import CSEServiceV1Impl from './api/services/CSEService/CSEServiceV1Impl'
import { getLnagForLocale } from './utils/locale'
import { Locale } from './service/translate/flights/flightTranslations.types'
import store from './store/BookingStore'
import getTitle from './logic/getTitle'
import { readWidgetModeFromUrl } from './store/widgetMode'
import { mapLangToLocale } from './utils/mapLangToLocale'
import type { LangTypes } from './types/langTypes'
import { isCSE } from './utils/cse'


const getContent = () => {
    if (getCookie('test_localization') === 'true')
        return localization['test_localization']
    if (getCookie('var_localization') === 'true')
        return localization['var_localization']

    const localeService = new LocaleServiceImpl()
    const locale: string = localeService.getLocale()

    const localeLocalization = localization[locale.replaceAll('-', '_').replace('_CSE', '')]

    if (!localeLocalization)
        throw new Error(
            'could not find localization for locale: ' + locale
        )
    return localeLocalization
};

const getYoonlyLocale = (website: string): Locale => {
    switch (website) {
        case "localhost":
        case "www.nl.yoonly.eu":
        case "www.benl.yoonly.eu":
            return "nl-NL" as Locale;
        case "dev.yoonly.eu":
        case "www.befr.yoonly.eu":
        case "www.yoonly.eu":
        default:
            return "fr-FR" as Locale;
    }
}

(async () => {
    const isLocal: boolean = window.location.hostname === 'localhost'
    window.env = await getEnvFile(isLocal)

    if (!window.env.VUE_APP_WEBSITE) {
        window.env.VUE_APP_WEBSITE = window.location.hostname;
    }
    if (window.location.hostname.endsWith(".yoonly.eu")) { // || window.location.hostname === "localhost"
        // Overwrite the website and locale
        window.env.VUE_APP_WEBSITE = window.location.hostname;
        window.env.VUE_APP_LOCALE = getYoonlyLocale(window.location.hostname) as Locale;
    }

    // Read widget-mode flags from URL once. When embedded inside the
    // train-results widget iframe, the host appends ?widgetMode=1&wm=cart|payment&lang=...&wuid=...&wpid=...
    const widgetMeta = readWidgetModeFromUrl()
    const urlParams = new URLSearchParams(window.location.search)
    const widgetLang = urlParams.get('lang') as LangTypes | null

    window.locale = window.env.VUE_APP_LOCALE
    if (widgetMeta.enabled && widgetLang) {
        window.locale = mapLangToLocale(widgetLang) as Locale
    }
    const isCSEContext = isCSE(window.locale as Locale)
    window.company = isCSEContext ? getCookie('company') : undefined;
    if (!widgetMeta.enabled) {
        injectHeaderFooter(window.env.VUE_APP_WEBSITE, window.locale, window.company || undefined);
    } else {
        document.body.classList.add('widget-mode')
    }
    document.documentElement.setAttribute('lang', window.locale)

    window.content = getContent()

    window.screenSizeManager = new ScreenSizeManager();
    window.screenSizeManager.init();

    // set page title
    // document.title = window.content['page_title']
    document.title = getTitle(window.env['VUE_APP_WEBSITE'] ?? 'Travelski') + ' - ' + window.content['page_title'];

    window.tfServices = {
        funnelService: new FunnelServiceV1Impl(),
        bookingService: new BookingServiceV1Impl(),
        optionFamiliesService: new OptionFamiliesServiceV1Impl(),
        catalogService: new CatalogServiceV1Impl(),
        priceService: new PriceServiceV1Impl(),
        contactService: new ContactServiceV1Impl(),
        paymentService: new PaymentServiceV1Impl(),
        orchestrator: new OrchestratorV1Impl(),
        packageCompositionService: new PackageCompositionV1Impl(),
        transportTimeLineService: new TransportTimeLineV1Impl(),
        serviceDurationService: new ServiceDurationV1Impl(),
        accomodationService: new AccomodationServiceV1Impl(),
        ...(isCSEContext && { CSEService: new CSEServiceV1Impl() })
    }

    if (window.env.VUE_APP_SINGLE_COMPANY_CONFIGURATION_URL && window.tfServices.CSEService) {
        const singleCompanyConfiguration = await window.tfServices.CSEService.getBookingFunnelSingleCompanyConfig()
        store.commit('setSingleCompanyConfiguration', singleCompanyConfiguration)
        bookigFunnelSingleCompanyConfig.value = singleCompanyConfiguration.bookingFunnelConfiguration
    }


    // VUE
    const app = createApp(BookingProcessApp)
    app.use(router)


    app.use(store)
    store.commit("setInitParams", {
        locale: window.locale as Locale,
        website: window.env['VUE_APP_WEBSITE']
    })
    store.commit("setWidgetMode", widgetMeta)

    initI18n(app, getLnagForLocale(window.locale as Locale, window.env['VUE_APP_WEBSITE'] ?? window.location.hostname))


    app.mount('#booking-process-app')


})();



